import { useState, useEffect } from "react";
import axios from "axios";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Eye, EyeSlash, LockSimple, Headset, WhatsappLogo, EnvelopeSimple, Fingerprint } from "@phosphor-icons/react";
import { getApiErrorMessage } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_WHATSAPP = "351960497651";
const ADMIN_EMAIL = "donald.valbuena@gresart.com";
const PASSKEY_DEVICE_FLAG = "gresart_passkey_registered_device";

export default function Login() {
  const { login, loginWithPasskey } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [showActivatePrompt, setShowActivatePrompt] = useState(false);
  const [activatingPasskey, setActivatingPasskey] = useState(false);

  useEffect(() => {
    if (window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setPasskeySupported)
        .catch(() => setPasskeySupported(false));
    }
  }, []);

  const goToApp = () => navigate(location.state?.from || "/", { replace: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Sessão iniciada com sucesso");
      // Every password login, on a device/browser that supports Face ID/Touch ID and hasn't
      // registered one yet, offer to activate it - keeps asking each time until accepted.
      if (passkeySupported && !localStorage.getItem(PASSKEY_DEVICE_FLAG)) {
        setShowActivatePrompt(true);
      } else {
        goToApp();
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Email ou senha incorretos"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivatePasskey = async () => {
    setActivatingPasskey(true);
    try {
      const { data: optionsJSON } = await axios.post(`${API}/auth/passkeys/register/options`);
      const credential = await startRegistration({ optionsJSON });
      await axios.post(`${API}/auth/passkeys/register/verify`, credential);
      localStorage.setItem(PASSKEY_DEVICE_FLAG, "true");
      toast.success("Face ID / Touch ID ativado neste dispositivo");
    } catch (error) {
      if (error?.name !== "NotAllowedError") {
        toast.error(getApiErrorMessage(error, "Não foi possível ativar o Face ID / Touch ID"));
      }
      // Cancelled or failed: don't set the flag, so it will ask again on the next login.
    } finally {
      setActivatingPasskey(false);
      setShowActivatePrompt(false);
      goToApp();
    }
  };

  const skipActivatePasskey = () => {
    setShowActivatePrompt(false);
    goToApp();
  };

  const handlePasskeyLogin = async () => {
    setPasskeyBusy(true);
    try {
      const { data: optionsJSON } = await axios.post(`${API}/auth/passkeys/login/options`);
      const assertion = await startAuthentication({ optionsJSON });
      await loginWithPasskey({ ...assertion, ceremony_id: optionsJSON.ceremony_id });
      localStorage.setItem(PASSKEY_DEVICE_FLAG, "true");
      toast.success("Sessão iniciada com sucesso");
      goToApp();
    } catch (error) {
      if (error?.name === "NotAllowedError") {
        toast.error("Cancelado, ou este dispositivo ainda não tem Face ID / Touch ID ativado");
      } else {
        toast.error(getApiErrorMessage(error, "Face ID / Touch ID não reconhecido. Inicie sessão com a sua senha e ative-o quando for convidado."));
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent("Olá, gostaria de solicitar acesso à app Gresart Showroom Control.");
    window.open(`https://wa.me/${ADMIN_WHATSAPP}?text=${text}`, "_blank");
  };

  const openEmail = () => {
    const subject = encodeURIComponent("Solicitação de acesso - Gresart Showroom Control");
    const body = encodeURIComponent("Olá,\n\nGostaria de solicitar acesso à app Gresart Showroom Control.\n\nObrigado.");
    window.location.href = `mailto:${ADMIN_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="login-page">
      {/* Subtle corporate green accents */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#009392]/8 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-[#009392]/6 blur-3xl" />
      <div className="absolute top-0 left-0 w-full h-1.5 bg-[#009392]" />

      <div className="w-full max-w-4xl relative z-10 flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-20">
        {/* Left: brand logos stacked (Gresart on top, TileHub below) */}
        <div className="flex flex-col items-center lg:items-start gap-10" data-testid="login-branding">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_ea05fd14-44c9-4674-8c56-754eef17102d/artifacts/c7elo6le_Gresart%20line.png"
            alt="GRESART Ceramic Tiles"
            className="h-16 sm:h-20 w-auto object-contain"
            data-testid="login-logo-gresart"
          />
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_ea05fd14-44c9-4674-8c56-754eef17102d/artifacts/gqlrhg0i_image.png"
            alt="TileHub by Donald Valbuena"
            className="h-40 sm:h-48 w-auto object-contain"
            data-testid="login-logo-tilehub"
          />
        </div>

        {/* Right: login form */}
        <div className="w-full max-w-sm flex-shrink-0">
        <form onSubmit={handleSubmit} className="bg-white border border-stone-200 shadow-xl shadow-stone-200/60 rounded-2xl p-7 space-y-5">
          <div className="flex items-center gap-2 justify-center text-[#009392] mb-1">
            <LockSimple size={18} weight="bold" />
            <span className="text-sm font-semibold uppercase tracking-wide">Acesso Restrito</span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="login-email" className="text-stone-600">Email</Label>
            <Input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              placeholder="nome@gresart.com"
              className="border-stone-300 focus-visible:ring-[#009392]"
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="login-password" className="text-stone-600">Senha</Label>
            <div className="relative">
              <Input
                id="login-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="border-stone-300 focus-visible:ring-[#009392] pr-10"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                data-testid="login-toggle-password-visibility"
              >
                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            className="w-full bg-[#009392] hover:bg-[#007a79] text-white font-semibold h-11 rounded-lg transition-colors"
            disabled={submitting}
            data-testid="login-submit-button"
          >
            {submitting ? "A entrar..." : "Entrar"}
          </Button>

          {passkeySupported && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-stone-200" />
                <span className="text-xs text-stone-400 uppercase tracking-wide">ou</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handlePasskeyLogin}
                disabled={passkeyBusy}
                className="w-full border-[#009392]/30 text-[#009392] hover:bg-[#009392]/5 h-11 rounded-lg font-medium"
                data-testid="login-passkey-button"
              >
                <Fingerprint size={20} className="mr-2" />
                {passkeyBusy ? "A verificar..." : "Entrar com Face ID / Touch ID"}
              </Button>
            </>
          )}
        </form>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-full mt-4 border-[#009392]/30 text-[#009392] hover:bg-[#009392]/5 h-11 rounded-lg font-medium"
              data-testid="btn-contact-admin"
            >
              <Headset size={18} className="mr-2" /> Contactar com o Administrador
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-64">
            <DropdownMenuItem onClick={openWhatsApp} data-testid="btn-contact-whatsapp" className="cursor-pointer">
              <WhatsappLogo size={18} className="mr-2 text-[#25D366]" /> Enviar WhatsApp
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openEmail} data-testid="btn-contact-email" className="cursor-pointer">
              <EnvelopeSimple size={18} className="mr-2 text-[#009392]" /> Enviar Email
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <p className="text-center text-xs text-stone-400 mt-6">
          &copy; {new Date().getFullYear()} GRESART - Acesso restrito a utilizadores autorizados
        </p>
        </div>
      </div>

      <Dialog open={showActivatePrompt} onOpenChange={(open) => !open && skipActivatePasskey()}>
        <DialogContent className="sm:max-w-sm" data-testid="passkey-activate-prompt">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-[#009392]/10 flex items-center justify-center mx-auto mb-2">
              <Fingerprint size={24} className="text-[#009392]" />
            </div>
            <DialogTitle className="text-center">Ativar Face ID / Touch ID?</DialogTitle>
            <DialogDescription className="text-center">
              Deseja ativar o Face ID / Touch ID para entrar mais rápido na próxima vez, sem escrever a senha?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={skipActivatePasskey}
              disabled={activatingPasskey}
              data-testid="passkey-activate-skip-button"
            >
              Agora não
            </Button>
            <Button
              type="button"
              className="flex-1 bg-[#009392] hover:bg-[#007a79] text-white"
              onClick={handleActivatePasskey}
              disabled={activatingPasskey}
              data-testid="passkey-activate-confirm-button"
            >
              {activatingPasskey ? "A ativar..." : "Ativar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
